import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { shouldOfferPlaceOnMap } from '@core/helpers/coordinate-placement.helper';
import { FilledButtonComponent } from '@custom-components/general/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { DeviceModel, DeviceResponseModel } from '@core/models/device.model';
import { Installation } from '@core/models/installation.model';
import { LocationModel, LocationUpdateModel } from '@core/models/location.model';
import { InstallationService } from '@core/services/installation.service';
import { CoordinateResult } from '@core/models/location-coordinates.model';
import { Service, ServiceResponseModel } from '@core/models/service.model';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { StatusChipComponent } from '@custom-components/general/status-chip/status-chip.component';
import { StatusBadgeType } from '@custom-components/general/status-chip/status-chip.component';
import {
  CoordinateMapDialogComponent,
  CoordinateMapDialogData,
  coordinateMapDialogConfig,
} from '../coordinate-map-dialog/coordinate-map-dialog.component';
import { BranchesMapComponent } from '../branches-map/branches-map.component';
import { LocationDialogComponent } from '../location-dialog/location-dialog.component';
import { LocationInstallationsCardComponent } from '../location-installations-card/location-installations-card.component';
import {
  LinkDeviceDialogComponent,
  LinkDeviceDialogData,
  LinkDeviceResult,
} from '../link-device-dialog/link-device-dialog.component';

// More than a location hosts in practice; the dialog lists every Active installation it is given.
const ACTIVE_INSTALLATIONS_LIMIT = 100;

export interface LocationLinkRequest extends LinkDeviceResult {
  // The dialog asked the user where the device goes.
  choiceShown: boolean;
}

/**
 * The status the location chip shows for a device. deviceOnlineStatus is checked against
 * last_readout freshness; deviceStatus is the raw last poll/push outcome and can keep saying online
 * for a device that stopped delivering, so it is only the fallback for a response without the field.
 */
export function deviceChipStatus(
  device: Pick<DeviceModel, 'deviceStatus' | 'deviceOnlineStatus'>,
): string {
  return device.deviceOnlineStatus ?? device.deviceStatus;
}

@Component({
  selector: 'xemex-location-general-tab',
  imports: [
    AsyncPipe,
    FilledButtonComponent,
    MatIconModule,
    TranslateModule,
    CardComponent,
    CardGridComponent,
    DetailFieldComponent,
    StatusChipComponent,
    BranchesMapComponent,
    LocationInstallationsCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './location-general-tab.component.html',
})
export class LocationGeneralTabComponent {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly installationService = inject(InstallationService);

  locationDetail = input<LocationModel>();
  isLoading = input<boolean>(false);
  permissions = input<string[]>();
  /** Active partner OID of the logged-in user (the Xemex placeholder OID for Xemex users). */
  userPartnerOid = input<string>();
  servicesByLocationId = input<ServiceResponseModel | undefined>();
  devicesByLocationId = input<DeviceResponseModel | undefined>();
  isLoadingServices = input<boolean>(false);
  isLoadingDevices = input<boolean>(false);
  /** Xemex staff holding view_installations; the Installations card is not shown otherwise. */
  showInstallations = input<boolean>(false);
  canCreateInstallation = input<boolean>(false);
  /** Changes when a link may have changed the installations at the location. */
  installationsRevision = input<number>(0);

  updateLocation = output<LocationUpdateModel>();
  viewPartner = output<string>();
  viewDevice = output<string>();
  linkDevice = output<LocationLinkRequest>();
  /** The pin was moved or the dialog found the location changed, so the page reads it again. */
  coordinatesChanged = output<void>();

  protected readonly previewServices = computed<Service[]>(
    () => this.servicesByLocationId()?.services?.slice(0, 3) ?? [],
  );

  protected readonly previewDevices = computed<DeviceModel[]>(
    () => this.devicesByLocationId()?.devices?.slice(0, 3) ?? [],
  );

  protected readonly servicesSubtitle = computed(() =>
    this.previewServices().length
      ? `Showing last ${this.previewServices().length} of ${this.servicesByLocationId()?.numberOfItems ?? 0}`
      : undefined,
  );

  protected readonly devicesSubtitle = computed(() =>
    this.previewDevices().length
      ? `${this.devicesByLocationId()?.numberOfItems ?? 0} total`
      : undefined,
  );

  protected readonly installationGroups = computed(
    () => this.locationDetail()?.installationGroups ?? [],
  );

  protected readonly canViewInstallationGroups = computed(
    () => this.permissions()?.includes('view_installation_groups') ?? false,
  );

  protected readonly canEditLocation = computed(() => {
    if (!this.permissions()?.includes('edit_location_details')) {
      return false;
    }
    const userPartnerOid = this.userPartnerOid();
    if (userPartnerOid === XEMEX_OID) {
      return true;
    }
    return !!userPartnerOid && userPartnerOid === this.locationDetail()?.partnerOid;
  });

  /**
   * Moving the pin can move the zone of another partner's installation, so Xemex staff only, and
   * only where the pin is the fallback: an address that could not be geocoded.
   */
  protected readonly canPlaceOnMap = computed(() => {
    const location = this.locationDetail();
    return (
      this.userPartnerOid() === XEMEX_OID &&
      !!this.permissions()?.includes('edit_location_details') &&
      !!location &&
      shouldOfferPlaceOnMap({
        postalCode: location.locationPostalCode,
        streetNumber: location.locationStreetNumber,
        latitude: location.locationLatitude,
        longitude: location.locationLongitude,
        coordinateSource: location.coordinateSource,
      })
    );
  });

  /** Same visibility as editing the location — Xemex staff, or the owning partner's own users. */
  protected readonly canLinkDevice = computed(() => {
    if (!this.locationDetail()?.partnerOid) {
      return false;
    }
    const userPartnerOid = this.userPartnerOid();
    if (userPartnerOid === XEMEX_OID) {
      return true;
    }
    return !!userPartnerOid && userPartnerOid === this.locationDetail()?.partnerOid;
  });

  get locationAddressDetails(): string {
    const d = this.locationDetail();
    if (!d) return '';
    return [d.locationStreet, d.locationStreetNumber, d.locationStreetNumberAddition]
      .filter(e => e != null && e !== '')
      .join(' ');
  }

  protected serviceChipStatus(status: string): StatusBadgeType {
    const map: Record<string, StatusBadgeType> = {
      Active: 'success',
      'Pending activation': 'warning',
      'Pending termination': 'warning',
      Terminated: 'error',
    };
    return map[status] ?? 'inactive';
  }

  onViewAllDevices(): void {
    this.router.navigate(['/', APP_PATHS.Devices], {
      queryParams: { locationId: this.locationDetail()?.locationId },
    });
  }

  onViewInstallationGroup(installationGroupOid: string): void {
    this.router.navigate(['/', APP_PATHS.InstallationGroups, installationGroupOid]);
  }

  protected readonly deviceChipStatus = deviceChipStatus;

  protected deviceOnline(status: string): boolean {
    return status?.toLowerCase().includes('online') ?? false;
  }

  onUpdate() {
    this.dialog
      .open(LocationDialogComponent, {
        width: '44rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: { mode: 'edit', location: this.locationDetail() },
      })
      .afterClosed()
      .subscribe((value: LocationUpdateModel) => {
        if (value) this.updateLocation.emit(value);
      });
  }

  onPlaceOnMap(): void {
    const locationId = this.locationDetail()?.locationId;
    if (!locationId) {
      return;
    }
    const ref = this.dialog.open<
      CoordinateMapDialogComponent,
      CoordinateMapDialogData,
      CoordinateResult
    >(CoordinateMapDialogComponent, coordinateMapDialogConfig(locationId));
    const editor = ref.componentInstance;
    ref.afterClosed().subscribe(result => {
      if (result !== undefined || editor.stale()) this.coordinatesChanged.emit();
    });
  }

  onLinkDevice(): void {
    const locationId = this.locationDetail()?.locationId;
    const partnerOid = this.locationDetail()?.partnerOid;
    if (!locationId || !partnerOid) {
      return;
    }
    if (!this.showInstallations()) {
      this.openLinkDialog(locationId, partnerOid, []);
      return;
    }
    // Without the installations, whether read or not, the dialog is the one every user gets.
    this.installationService
      .list({ locationId, status: 'Active', limit: ACTIVE_INSTALLATIONS_LIMIT, offset: 0 })
      .pipe(
        map(response => response.installations),
        catchError(() => of<Installation[]>([])),
      )
      .subscribe(installations => this.openLinkDialog(locationId, partnerOid, installations));
  }

  private openLinkDialog(
    locationId: number | string,
    partnerOid: string,
    installations: Installation[],
  ): void {
    const choiceShown = installations.length > 0;
    this.dialog
      .open<LinkDeviceDialogComponent, LinkDeviceDialogData, LinkDeviceResult>(
        LinkDeviceDialogComponent,
        {
          width: '48rem',
          maxWidth: 'calc(100vw - 2rem)',
          maxHeight: 'calc(100vh - 2rem)',
          data: { locationId, partnerOid, ...(choiceShown ? { installations } : {}) },
        },
      )
      .afterClosed()
      .subscribe(result => {
        if (result) this.linkDevice.emit({ ...result, choiceShown });
      });
  }
}
